<script setup lang="ts">
import * as Sentry from '@sentry/vue';
import { useRoute, useRouter } from 'vue-router';
import { useImmersveAuthStore } from '@/stores/auth.store';
import Loading from '@/components/ui/Loading.vue';
import { determineRouteToRedirect } from '@/util/sessionRedirector';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { wellKnownRoutes } from '@/routes/wellknown';
import Mixpanel from '@/integrations/Mixpanel';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

const route = useRoute();
const router = useRouter();
const sessionId = route?.params.sessionid as string;
const authCode = route?.query.code as string;

const authStore = useImmersveAuthStore();
const kycSessionStore = useKycSessionStore();

async function init() {
  authStore.initialiseSession(sessionId);

  if (authCode) {
    try {
      await authStore.obtainTokens(authCode);
    } catch (error) {
      Sentry.captureException(error);
      await router.replace({ name: wellKnownRoutes.Timeout });
      return;
    }

    const url = new URL(document.location.href);
    url.search = '';
    window.history.replaceState({}, document.title, url.toString());
  } else if (authStore.hasInvalidAccessToken()) {
    if (authStore.hasInvalidRefreshToken()) {
      await router.replace({ name: wellKnownRoutes.Timeout });
      return;
    }

    try {
      await authStore.refreshTokens();
    } catch (error) {
      Sentry.captureException(error);
      await router.replace({ name: wellKnownRoutes.Timeout });
      return;
    }
  }

  await kycSessionStore.getSessionData();

  const accountId = kycSessionStore.kycSession?.cardholder.accountId;
  Mixpanel.instance.identify(accountId);

  if (!kycSessionStore.kycSession) {
    return router.replace({ name: wellKnownRoutes.Timeout });
  }

  await kycSessionStore.kycSession.start();

  const routeToRedirect = determineRouteToRedirect(kycSessionStore.kycSession);

  await router.replace(routeToRedirect);
}

init();
</script>

<template>
  <KycSessionLayout>
    <Loading data-test="loading-session-initiation" class="mx-auto mt-10 h-10 w-10" />
  </KycSessionLayout>
</template>
